import {
  HTMLProps,
  ReactNode,
  SetStateAction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ForwardedRef,
  useMemo,
} from 'react'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { Button, Flex, Skeleton, Tag } from 'antd'
import isNil from 'lodash.isnil'
import styled, { css } from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { TextSize, getFontSize } from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'
import { filterNil } from 'src/utils'
import { formatNumber, NumberFormat } from 'src/utils/number'

import HStack from '../Flex/HStack'
import Menu from '../Menu/Menu'
import Portal from '../Portal/Portal'
import { T } from '../Text/Text'
import Tooltip from '../Tooltip/Tooltip'

const TAB_CONTAINER_KEY_ATTR = 'data-govwell-tab-container-key'
const TAB_KEY_ATTR = 'data-govwell-tab-key'

const StyledTabsPrimitiveRoot = styled(TabsPrimitive.Root)`
  max-width: 100%;
  ic-anchor-point {
    margin: -3px; // The intercom tooltip anchor point is injected into the DOM and is counted as a flex child, causing spurious gap in the flex container
  }
`

const getTabKey = <TKey extends string>(el: Element | undefined): TKey | undefined => {
  return el?.getAttribute(TAB_KEY_ATTR) as TKey | undefined
}

export type TabItem<TKey extends string> = {
  children?: never
  count?: number
  countIsLoading?: boolean
  key: TKey
  label: ReactNode
  renderChildren?: () => ReactNode
  renderTab?: (args: { children: ReactNode }) => ReactNode
  tooltip?: string
}

type TabsBaseProps<TKey extends string> = {
  actions?: ReactNode
  isLoading?: boolean
  onReorderTabs?: (tabKeys: TKey[]) => void
  onSelectedTabKeyChange: (tabKey: TKey) => void
  selectedTabKey: TKey
  tabs: TabItem<TKey>[]
}
const TabsBase = <TKey extends string>({
  actions,
  isLoading,
  onReorderTabs,
  onSelectedTabKeyChange: onSelectedKeyChange,
  selectedTabKey,
  tabs,
}: TabsBaseProps<TKey>) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const tabListContainerRef = useRef<HTMLDivElement>(null)
  const tabListRef = useRef<HTMLDivElement>(null)

  const [tabsInternal, setTabsInternal] = useState<TabItem<TKey>[]>(tabs)
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  useEffect(() => {
    setTabsInternal(tabs)
  }, [tabs])

  const [tabContentsByTabKey, setTabContentsByTabKey] = useState<Map<TKey, ReactNode>>(
    new Map<TKey, ReactNode>()
  )
  const [tabKeys, setTabKeys] = useState<Set<TKey>>(new Set<TKey>())
  const [visibleTabKeys, setVisibleTabKeys] = useState<Set<TKey>>(new Set<TKey>())

  const getTabContainerElement = useCallback((tabKey: TKey): Element | null => {
    return rootRef.current
      ? rootRef.current.querySelector(`[${TAB_CONTAINER_KEY_ATTR}="${tabKey}"]`)
      : null
  }, [])
  const getTabContainerElements = useCallback(
    (): Element[] =>
      rootRef.current
        ? Array.from(rootRef.current.querySelectorAll(`[${TAB_CONTAINER_KEY_ATTR}]`).values())
        : [],
    []
  )
  const getTabElement = useCallback((tabKey: TKey): Element | null => {
    return rootRef.current ? rootRef.current.querySelector(`[${TAB_KEY_ATTR}="${tabKey}"]`) : null
  }, [])
  const getTabElements = useCallback(
    (): Element[] =>
      rootRef.current
        ? Array.from(rootRef.current.querySelectorAll(`[${TAB_KEY_ATTR}]`).values())
        : [],
    []
  )

  const handleReorderTabs = useCallback(
    (newTabOrder: TKey[]) => {
      const tabsByKey = new Map<TKey, TabItem<TKey>>(tabsRef.current.map((tab) => [tab.key, tab]))
      setTabsInternal(newTabOrder.map((tabKey) => tabsByKey.get(tabKey) as TabItem<TKey>))
      onReorderTabs?.(newTabOrder)
    },
    [onReorderTabs]
  )

  const selectTab = useCallback(
    (newlySelectedTabKey: TKey) => {
      // Get all the relevant elements, bail out if the DOM queries fail
      const currentlySelectedTabContainer = getTabContainerElement(selectedTabKey)
      const currentlySelectedTab = getTabElement(selectedTabKey)
      const newlySelectedTab = getTabElement(newlySelectedTabKey)
      if (
        !tabListRef.current ||
        !newlySelectedTab ||
        !currentlySelectedTab ||
        !currentlySelectedTabContainer
      ) {
        return
      }

      // Get the total width of the tab container minus the total width of all of its tab children
      const visibleTabWidth = Array.from(visibleTabKeys.values())
        .map((tabKey) => getTabContainerElement(tabKey))
        .reduce((total, tabElement) => total + (tabElement?.clientWidth ?? 0), 0)
      const listWidth = tabListRef.current.clientWidth
      let availableWidth = listWidth - visibleTabWidth

      const tabElements = getTabElements()
      const orderedVisibleTabs = tabElements.filter((el) => {
        const k = getTabKey<TKey>(el)
        if (!k) {
          return false
        }
        return visibleTabKeys.has(k)
      })
      const orderedInvisibleTabs = tabElements.filter((el) => {
        const k = getTabKey<TKey>(el)
        if (!k) {
          return false
        }
        return k !== newlySelectedTabKey && !visibleTabKeys.has(k)
      })

      // Displace tabs as necessary until the newly selected tab can fit in the available space
      const tabKeysToDisplace: TKey[] = []
      const newlySelectedTabWidth = newlySelectedTab.clientWidth
      while (availableWidth < newlySelectedTabWidth) {
        if (orderedVisibleTabs.length === 0) {
          break
        }
        const tabToDisplace = orderedVisibleTabs.pop()
        const tabKeyToDisplace = getTabKey<TKey>(tabToDisplace)
        if (tabToDisplace && tabKeyToDisplace) {
          tabKeysToDisplace.unshift(tabKeyToDisplace)
          availableWidth += tabToDisplace.clientWidth
        }
      }

      // Update the tab order and selected tab
      const orderedVisibleTabKeys = filterNil(orderedVisibleTabs.map((el) => getTabKey<TKey>(el)))
      const orderedInvisibleTabKeys = filterNil(
        orderedInvisibleTabs.map((el) => getTabKey<TKey>(el))
      )
      const visibleKeys: TKey[] = [...orderedVisibleTabKeys, newlySelectedTabKey]
      const invisibleKeys: TKey[] = [...tabKeysToDisplace, ...orderedInvisibleTabKeys]
      const newTabOrder: TKey[] = [...visibleKeys, ...invisibleKeys]
      setVisibleTabKeys(new Set([...orderedVisibleTabKeys, newlySelectedTabKey]))
      handleReorderTabs(newTabOrder)
      onSelectedKeyChange(newlySelectedTabKey)
    },
    [
      getTabContainerElement,
      selectedTabKey,
      getTabElement,
      visibleTabKeys,
      getTabElements,
      handleReorderTabs,
      onSelectedKeyChange,
    ]
  )

  const handleValueChange = useCallback(
    (value: string) => {
      onSelectedKeyChange(value as TKey)
    },
    [onSelectedKeyChange]
  )

  return (
    <TabsContext.Provider
      value={{
        getTabContainerElement,
        getTabContainerElements,
        getTabElement,
        getTabElements,
        rootRef,
        selectedTabKey,
        selectTab,
        setTabContentsByTabKey,
        setTabKeys,
        setVisibleTabKeys,
        tabContentsByTabKey,
        tabListContainerRef,
        tabKeys,
        tabListRef,
        visibleTabKeys,
      }}
    >
      <StyledTabsPrimitiveRoot
        ref={rootRef}
        value={selectedTabKey}
        onValueChange={handleValueChange}
        activationMode="manual"
      >
        {isLoading && (
          <HStack gap={2}>
            {[0, 1, 2].map((i) => (
              <Skeleton.Input key={`loading-state-${i}`} active />
            ))}
          </HStack>
        )}
        {!isLoading && (
          <TabsList>
            {tabsInternal.map((tab) => {
              const tabNode = (
                <Tab
                  key={tab.key}
                  count={tab.count}
                  countIsLoading={tab.countIsLoading}
                  label={tab.label}
                  tabKey={tab.key}
                  tooltip={tab.tooltip}
                />
              )
              return tab.renderTab ? tab.renderTab({ children: tabNode }) : tabNode
            })}
          </TabsList>
        )}
        {actions ? <Actions>{actions}</Actions> : null}
      </StyledTabsPrimitiveRoot>
    </TabsContext.Provider>
  )
}

const StyledTabListContainer = styled(Flex)`
  align-items: center;
  position: relative;
  max-width: 100%;
  gap: 6px;
`
const StyledTabList = styled(TabsPrimitive.List)`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow-x: hidden;
  height: 46px;
`
const StyledMoreMenuButton = styled(Button)`
  color: ${({ theme }) => theme.colorText};
`
type TabsListProps = {
  children?: React.ReactNode
}
const TabsList = ({ children }: TabsListProps) => {
  const {
    getTabContainerElements,
    tabListContainerRef,
    tabListRef,
    selectedTabKey,
    selectTab,
    setVisibleTabKeys,
    setTabKeys,
    visibleTabKeys,
  } = useContext(TabsContext)

  const tabContainerElements = getTabContainerElements()

  // Create a stable dependency that resets the observer when tabs are added or removed
  const tabKeySequence = tabContainerElements
    .map((el) => el.getAttribute(TAB_CONTAINER_KEY_ATTR))
    .sort()
    .join(',')

  useEffect(() => {
    if (!tabListRef.current) {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const tabKey = e.target.getAttribute(TAB_CONTAINER_KEY_ATTR)
          if (!tabKey) {
            return
          }
          setVisibleTabKeys((prevState: Set<string>) => {
            const newState = new Set<string>(prevState)
            if (e.intersectionRatio === 1) {
              newState.add(tabKey)
            } else {
              newState.delete(tabKey)
            }
            return newState
          })
        })
      },
      {
        root: tabListRef.current,
        threshold: 1,
      }
    )
    const containerElements = getTabContainerElements()
    containerElements.forEach((el) => {
      observer.observe(el)
    })
    setTabKeys(
      new Set(filterNil(containerElements.map((el) => el.getAttribute(TAB_CONTAINER_KEY_ATTR))))
    )
    return () => observer.disconnect()
  }, [getTabContainerElements, tabListRef, setTabKeys, setVisibleTabKeys, tabKeySequence])

  const isAnyTabVisible = visibleTabKeys.size > 0
  const isSelectedTabKeyVisible = visibleTabKeys.has(selectedTabKey)
  useEffect(() => {
    if (isAnyTabVisible && !isSelectedTabKeyVisible) {
      selectTab(selectedTabKey)
    }
  }, [isAnyTabVisible, isSelectedTabKeyVisible, selectTab, selectedTabKey])

  return (
    <StyledTabListContainer
      ref={tabListContainerRef}
      className="before:absolute before:inset-0 before:w-full before:border-b before:border-neutral-300 before:content-['']"
    >
      <StyledTabList ref={tabListRef}>{children}</StyledTabList>
    </StyledTabListContainer>
  )
}

const StyledTabContainer = styled(Flex)`
  align-items: center;
  position: relative;
  max-width: 100%;
`
const ActiveTabStyles = css`
  &::before {
    content: '';
    position: absolute;
    left: 0px;
    bottom: 0px;
    border-bottom: solid 2px ${({ theme }) => theme.colorPrimaryBase};
    margin: 0 0 0 1px;
    width: calc(100% - 2px);
    pointer-events: none;
  }
`
const StyledTab = styled(TabsPrimitive.TabsTrigger)<{
  $isVisible: boolean
}>`
  position: relative;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: ${getFontSize(TextSize.Base)}px;
  font-weight: 400;
  border: none;
  outline: solid 2px transparent;
  cursor: pointer;
  max-width: 100%;
  padding: 0px 12px;
  color: ${({ theme }) => theme.colorText};

  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};

  &[data-state='active'] {
    ${ActiveTabStyles}
  }
`

type TabProps<TKey extends string> = Pick<
  HTMLProps<HTMLButtonElement>,
  'onClick' | 'onContextMenu'
> & {
  label: React.ReactNode
  count?: number
  countIsLoading?: boolean
  tabKey: TKey
  tooltip?: string
}
const Tab = forwardRef(
  <TKey extends string>(
    { tabKey, label, count, countIsLoading, onClick, onContextMenu, tooltip }: TabProps<TKey>,
    tabRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { getTabElements, tabKeys, visibleTabKeys, setTabContentsByTabKey } = useContext(
      TabsContext
    ) as TabsContextType<TKey>
    const isVisible = visibleTabKeys?.has(tabKey) ?? true
    const visibleTabCount = visibleTabKeys?.size ?? 0
    const index = filterNil(getTabElements().map((el) => el.getAttribute(TAB_KEY_ATTR)))
      .filter((k) => visibleTabKeys?.has(k as TKey))
      .indexOf(tabKey)
    const isLastVisibleTab = isVisible && index === visibleTabCount - 1
    const areAnyTabsHidden = tabKeys && visibleTabKeys && tabKeys.size !== visibleTabKeys.size

    const tabContents = useMemo(
      () => (
        <HStack gap={1} justify="space-between">
          <T.BodyLarge className="govwell-tab-label" maxLines={1}>
            {label}
          </T.BodyLarge>
          {countIsLoading ? (
            <Skeleton.Button active size="small" className="!h-[22px] !w-[32px] !min-w-[32px]" />
          ) : (
            !isNil(count) && (
              <Tag color="default" style={{ margin: 0 }}>
                {formatNumber(count, { format: NumberFormat.Integer })}
              </Tag>
            )
          )}
        </HStack>
      ),
      [label, count, countIsLoading]
    )

    useEffect(() => {
      setTabContentsByTabKey((prevState) => {
        const newState = new Map<TKey, ReactNode>(prevState)
        newState.set(tabKey, tabContents)
        return newState
      })
    }, [setTabContentsByTabKey, tabContents, tabKey])

    const baseClassNames =
      'relative [&_.govwell-tab-label]:text-neutral-800 bg-transparent hover:[&_.govwell-tab-hover-background]:visible'
    const activeClassNames =
      "data-[state='active']:[&_.govwell-tab-label]:font-semibold data-[state='active']:[&_.govwell-tab-label]:text-neutral-950"
    const focusIndicatorClassNames =
      'after:absolute after:inset-[4px_4px_6px_4px] after:rounded-sm after:outline-outline after:content-[""] focus-visible:after:outline-1'

    const tabTrigger = (
      <StyledTab
        value={tabKey}
        $isVisible={isVisible}
        onClick={onClick}
        onContextMenu={onContextMenu}
        {...{ [TAB_KEY_ATTR]: tabKey }}
        className={twMerge(baseClassNames, activeClassNames, focusIndicatorClassNames)}
      >
        <div className="govwell-tab-hover-background bg-neutral-150 invisible absolute inset-[4px_4px_6px_4px] z-[-1] rounded-sm" />
        {tabContents}
      </StyledTab>
    )

    return (
      <StyledTabContainer
        ref={tabRef}
        {...{ [TAB_CONTAINER_KEY_ATTR]: tabKey }}
        className="isolate"
      >
        {tooltip ? (
          <Tooltip content={tooltip}>
            <span className="inline-flex h-full">{tabTrigger}</span>
          </Tooltip>
        ) : (
          tabTrigger
        )}
        {isLastVisibleTab && areAnyTabsHidden && <MoreMenu />}
      </StyledTabContainer>
    )
  }
)

const MoreMenu = () => {
  const { tabKeys, visibleTabKeys, tabContentsByTabKey, selectTab } = useContext(TabsContext)

  const { isOpen, open, close } = useDisclosure()
  return (
    <Menu isOpen={isOpen} onOpen={open} onClose={close}>
      <Menu.Trigger>
        <StyledMoreMenuButton icon={<FontAwesomeIcon icon={faEllipsisVertical} />} type="link">
          {`${tabKeys.size - visibleTabKeys.size} more`}
        </StyledMoreMenuButton>
      </Menu.Trigger>
      <Menu.Content>
        {Array.from(tabKeys.values())
          .filter((k) => !visibleTabKeys.has(k))
          .map((k) => (
            <Menu.Item key={k} onClick={() => selectTab(k)}>
              {tabContentsByTabKey.get(k)}
            </Menu.Item>
          ))}
      </Menu.Content>
    </Menu>
  )
}

type ActionsProps = {
  children?: React.ReactNode
}
const Actions = ({ children }: ActionsProps) => {
  const { tabListContainerRef } = useContext(TabsContext)
  if (!tabListContainerRef.current) {
    return null
  }
  return <Portal container={tabListContainerRef.current}>{children}</Portal>
}

type TabsContextType<TKey extends string> = {
  getTabContainerElement: (tabKey: TKey) => Element | null
  getTabContainerElements: () => Element[]
  getTabElement: (tabKey: TKey) => Element | null
  getTabElements: () => Element[]
  rootRef: React.RefObject<HTMLDivElement>
  selectTab: (tabKey: TKey) => void
  selectedTabKey: TKey
  setTabContentsByTabKey: React.Dispatch<SetStateAction<Map<TKey, ReactNode>>>
  setTabKeys: React.Dispatch<SetStateAction<Set<TKey>>>
  setVisibleTabKeys: React.Dispatch<SetStateAction<Set<TKey>>>
  tabContentsByTabKey: Map<TKey, ReactNode>
  tabKeys: Set<TKey>
  tabListContainerRef: React.RefObject<HTMLDivElement>
  tabListRef: React.RefObject<HTMLDivElement>
  visibleTabKeys: Set<TKey>
}
const TabsContext = React.createContext<TabsContextType<any>>({} as TabsContextType<any>)

export default TabsBase
